import { zodResolver } from '@hookform/resolvers/zod'
import { Language, z } from '@repo/ui/lang'
import { Cfl } from '@repo/ui/utils'
import { enUS, es, fr, Locale, pt } from 'date-fns/locale'
import { TOptionsBase } from 'i18next'
import { isEqual } from 'lodash'
import {
  FormEvent,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { DefaultValues, Mode, Path, useForm } from 'react-hook-form'
import { useTranslation, UseTranslationOptions } from 'react-i18next'
import { ZodRawShape, ZodType } from 'zod'

export type Options = TOptionsBase &
  Record<string, unknown> & {
    /**
     * Capitalize first letter of the first word
     */
    cfl?: boolean
    /**
     * Add colon suffix
     */
    colon?: boolean
  }

export function useT(options?: UseTranslationOptions<string>) {
  const translation = useTranslation()

  const isEN = (translation.i18n.language as Language) === Language.EN

  const processTranslation = (key: string, customOptions: Options, opts?: Options): string => {
    let result = translation.t(key, customOptions)

    if (opts?.cfl) {
      // Capitalize first letter
      result = Cfl(result)
    }

    if (opts?.colon) {
      // Add colon suffix
      const optionalExtraSpace = isEN ? '' : ' '
      const addColonSuffix = (str: string) => `${str}${optionalExtraSpace}:`
      result = addColonSuffix(result)
    }

    return result
  }

  const t = (key: string, opts?: Options): string => {
    const customOptions = { ns: 'translation', ...options, ...opts }
    return processTranslation(key, customOptions, opts)
  }

  const tc = (key: string, opts?: Options): string => {
    const customOptions = { ns: 'common', ...opts }
    return processTranslation(key, customOptions, opts)
  }

  const setLanguage = async (
    language: Language,
    cb?: (language: Language) => void,
  ): Promise<Language> => {
    await translation.i18n.changeLanguage(language)
    const lang = translation.i18n.language as Language
    cb?.(lang)
    return lang
  }

  return {
    i18n: translation.i18n,
    isEN,
    setLanguage,
    t,
    tc,
  }
}

export const getLocale = (): Locale => {
  const userLang = (localStorage.getItem('i18nextLng') as Language | null) || Language.EN
  const locale =
    userLang === Language.FR
      ? fr
      : userLang === Language.ES
        ? es
        : userLang === Language.PT
          ? pt
          : enUS
  return locale
}

export type LifeCycle =
  | ({
      onMounted?: () => void
      onUnmounted?: () => void
    } & ( // Ensures at least one of the two must be defined
      | { onMounted: () => void; onUnmounted?: undefined }
      | { onMounted?: undefined; onUnmounted: () => void }
      | { onMounted: () => void; onUnmounted: () => void }
    ))
  | undefined
export const useLifeCycle = (callbacks: LifeCycle) => {
  if (!callbacks) return
  useEffect(() => {
    callbacks.onMounted?.()
    return () => {
      callbacks.onUnmounted?.()
    }
  }, [])
}

export const useScrollToEnd = (
  containerRef: React.RefObject<HTMLDivElement>,
  behavior: ScrollBehavior = 'instant',
) => {
  const scrollToEnd = useCallback(() => {
    if (!containerRef.current) return
    containerRef.current.scrollIntoView({
      behavior,
      block: 'end',
    })
  }, [containerRef, behavior])
  return scrollToEnd
}

export const useWait = (enable: boolean, delay: number = 1000): boolean => {
  if (!enable) return false
  const [isLoading, setIsLoading] = useState<boolean>(enable)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsLoading(false)
    }, delay)
    // Cleanup timeout on component unmount
    return () => clearTimeout(timeoutId)
  }, [enable])
  return isLoading
}

export const useFormValidator = <Z extends ZodType, T extends ZodRawShape>({
  customValidation, // Will only be called AFTER the zod validation has been executed without any errors
  defaultValues,
  mode = 'all',
  onSubmit,
  schema,
}: {
  customValidation?: (values: z.infer<z.ZodObject<T>>) => Record<string, string>
  defaultValues: DefaultValues<z.infer<Z>>
  mode?: Mode
  onSubmit: (values: z.infer<z.ZodObject<T>>) => Promise<void> | void
  schema: T
}) => {
  const formSchema = z.object(schema)
  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues,
    mode,
    resolver: zodResolver(formSchema),
    reValidateMode: 'onSubmit',
    shouldFocusError: true,
  })

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues])

  const isFormValid = form.formState.isValid && !isEqual(form.getValues(), defaultValues)

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    let errors = {}
    if (customValidation) {
      errors = customValidation(values)
      Object.entries(errors).forEach(([key, message]) => {
        form.setError(key as Path<z.infer<typeof formSchema>>, {
          message: message as string | undefined,
        })
      })
    }
    if (isFormValid && Object.keys(errors).length === 0) {
      void onSubmit(values)
    }
  }

  const submit = (
    e?:
      | KeyboardEvent<HTMLFormElement>
      | ReactMouseEvent<HTMLButtonElement, MouseEvent>
      | FormEvent<HTMLFormElement>,
  ) => void form.handleSubmit(handleSubmit)(e)

  const submitOnPressMetaEnter = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.metaKey && e.key === 'Enter') submit(e)
  }

  return {
    form,
    isFormValid,
    submit,
    submitOnPressMetaEnter,
  }
}

export function useControlledOpen({
  openProp,
  defaultOpen,
  onOpenChange,
}: {
  openProp?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}): [boolean, (open: boolean) => void] {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? false)
  const isControlled = openProp !== undefined
  const open = isControlled ? openProp : uncontrolledOpen
  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next)
      if (onOpenChange) onOpenChange(next)
    },
    [isControlled, onOpenChange],
  )
  return [open, setOpen]
}

export function useLocalStorage<T>(key: string, initialValue: T) {
  const getValue = useCallback((): T => {
    try {
      const item = localStorage.getItem(key)
      return item ? (JSON.parse(item) as T) : initialValue
    } catch (error) {
      console.error(`Error loading localStorage key "${key}":`, error)
      return initialValue
    }
  }, [key, initialValue])

  const [storedValue, setStoredValue] = useState<T>(getValue)

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value
      setStoredValue(valueToStore)
      localStorage.setItem(key, JSON.stringify(valueToStore))
    } catch (error) {
      console.error(`Error saving localStorage key "${key}":`, error)
    }
  }

  useEffect(() => {
    // Onmount
    const value = getValue()
    setStoredValue(value)
    setValue(value)
  }, [key])

  return [storedValue, setValue] as const
}
